import React from "react";
import { Button, Chip, makeStyles, Tooltip } from "@material-ui/core";
import RefreshIconMui from "@material-ui/icons/Refresh";
import { AiOutlineExclamation } from "react-icons/ai";
import {
  BsFillPencilFill,
  BsFillStickyFill,
  BsFillTrashFill,
  BsListOl,
  BsFillPrinterFill,
  BsFillPersonCheckFill,
  BsFillPersonDashFill,
} from "react-icons/bs";
import { FaArchive, FaRegNewspaper, FaTruck } from "react-icons/fa";
import { GiHazardSign, GiTestTubes } from "react-icons/gi";
import { BiMoveHorizontal, BiSolidReport } from "react-icons/bi";
import { HiLockOpen, HiLockClosed } from "react-icons/hi";
import { IoMdCheckmark, IoMdCheckmarkCircleOutline, IoMdFlag } from "react-icons/io";
import { MdCallReceived, MdOutlineEditOff, MdOutlinePlaylistRemove } from "react-icons/md";
import { TbSalt } from "react-icons/tb";

import { history } from "../..";
import { Ticket } from "../../interface/api/Ticket";
import { User } from "../../interface/api/User";
import { isAdmin } from "../../utils/constants";
import { isDateEqualTo } from "../../utils/date-helper";

const useStyles = makeStyles({
  "@global": {
    "@keyframes spin": {
      from: { transform: "rotate(0deg)" },
      to: { transform: "rotate(360deg)" },
    },
  },
  actionIconGroup: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    flexWrap: "nowrap",
  },
  // Spread variant: fills the cell width and distributes the action icons evenly.
  // Used by the non-dashboard data tables.
  actionIconGroupSpread: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-evenly",
    width: "100%",
    flexWrap: "nowrap",
  },
  statusIconGroup: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexWrap: "nowrap",
    minHeight: 24,
  },
  actionIcon: {
    cursor: "pointer",
    fontSize: 18,
    lineHeight: 1,
    transition: "opacity 0.2s ease, transform 0.2s ease",
    "&:hover": { opacity: 0.72, transform: "translateY(-1px)" },
  },
  statusIcon: {
    fontSize: 18,
    lineHeight: 1,
    cursor: "default",
    transition: "opacity 0.2s ease",
    "&:hover": { opacity: 0.82 },
  },
  primaryAction: {
    color: "#032e5b",
  },
  warningAction: {
    color: "#c48a00",
  },
  dangerAction: {
    color: "#c62828",
  },
  neutralAction: {
    color: "#374151",
  },
  positiveStatus: {
    color: "#2e7d32",
  },
  warningStatus: {
    color: "#ef6c00",
  },
  dangerStatus: {
    color: "#c62828",
  },
  neutralStatus: {
    color: "#4b5563",
  },
  receiveIcon: {
    minWidth: 96,
    marginTop: 10,
    marginBottom: 10,
  },
  receiveIconCompact: {
    minWidth: 64,
    marginTop: 6,
    marginBottom: 6,
    padding: "4px 10px",
    fontSize: "12px",
  },
  dispatchLockOpenIcon: {
    cursor: "pointer",
    fontSize: 18,
    color: "#032e5b",
    transition: "opacity 0.2s ease",
    "&:hover": { opacity: 0.7 },
  },
  dispatchedClosedLockIcon: {
    cursor: "pointer",
    fontSize: 18,
    color: "#6b7280",
    transition: "opacity 0.2s ease",
    "&:hover": { opacity: 0.7 },
  },
  editDisabledIcon: {
    color: "#9ca3af",
    fontSize: 18,
  },
  archiveIcon: {
    fontSize: 18,
    color: "#6d4c41",
  },
});

const ActionIconGroup = ({
  children,
  spread,
}: {
  children: React.ReactNode;
  spread?: boolean;
}) => {
  const classes = useStyles();
  return (
    <div
      className={
        spread ? classes.actionIconGroupSpread : classes.actionIconGroup
      }
    >
      {children}
    </div>
  );
};

const StatusIconGroup = ({ children }: { children: React.ReactNode }) => {
  const classes = useStyles();
  return <div className={classes.statusIconGroup}>{children}</div>;
};

const IconTooltip = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => {
  return (
    <Tooltip title={title} placement="top" arrow enterDelay={150}>
      <span>{children}</span>
    </Tooltip>
  );
};

const ReceivedIcon = () => {
  const classes = useStyles();
  return (
    <IconTooltip title="Received">
      <IoMdCheckmark
        className={`${classes.statusIcon} ${classes.positiveStatus}`}
      />
    </IconTooltip>
  );
};

const NotCompleteIcon = () => {
  const classes = useStyles();
  return (
    <IconTooltip title="Missing Information">
      <AiOutlineExclamation
        className={`${classes.statusIcon} ${classes.dangerStatus}`}
      />
    </IconTooltip>
  );
};

const PendingHeadspaceIcon = () => {
  const classes = useStyles();
  return (
    <IconTooltip title="Pending Headspace">
      <GiTestTubes
        className={`${classes.statusIcon} ${classes.warningStatus}`}
      />
    </IconTooltip>
  );
};

const OverThresholdIcon = () => {
  const classes = useStyles();
  return (
    <IconTooltip title="Over Threshold">
      <GiHazardSign
        className={`${classes.statusIcon} ${classes.warningStatus}`}
      />
    </IconTooltip>
  );
};

const ScreeningFlagIcon = () => {
  const classes = useStyles();
  return (
    <IconTooltip title="Screening Flag">
      <IoMdFlag
        className={`${classes.statusIcon} ${classes.dangerStatus}`}
      />
    </IconTooltip>
  );
};

const SoilFLOIcon = () => {
  const classes = useStyles();
  return (
    <IconTooltip title="SoilFLO">
      <FaTruck className={`${classes.statusIcon} ${classes.warningStatus}`} />
    </IconTooltip>
  );
};

const TruckDispatchIcon = (
  row: Ticket,
  onToggleTicketDispatchLock: Function,
) => {
  const classes = useStyles();
  if (
    !isDateEqualTo(row.timeLoaded, new Date()) ||
    row.isReceived ||
    row.isSoilFLOLoad
  ) {
    return null;
  }

  return row.isDispatched ? (
    <IconTooltip title="Unlock Ticket Dispatch">
        <HiLockOpen
          className={classes.dispatchLockOpenIcon}
          onClick={() => onToggleTicketDispatchLock(row.id)}
        />
    </IconTooltip>
  ) : (
    <IconTooltip title="Lock Ticket Dispatch">
        <HiLockClosed
          className={classes.dispatchedClosedLockIcon}
          onClick={() => onToggleTicketDispatchLock(row.id)}
        />
    </IconTooltip>
  );
};

const TicketEditIcon = (row: Ticket, user: User | null) => {
  const classes = useStyles();
  return user && isAdmin(user.roleId) ? (
    <IconTooltip title="Edit Ticket">
        <EditIcon onClick={() => history.push(`/ticket/${row.id}`)} />
    </IconTooltip>
  ) : row.isReceived ? (
    <IconTooltip title="Disabled">
        <MdOutlineEditOff className={classes.editDisabledIcon} />
    </IconTooltip>
  ) : (
    <IconTooltip title="Edit Ticket">
        <EditIcon onClick={() => history.push(`/ticket/${row.id}`)} />
    </IconTooltip>
  );
};

const EditIcon = ({ onClick }: any) => {
  const classes = useStyles();
  return (
    <IconTooltip title="Edit">
        <BsFillPencilFill
          onClick={onClick}
          className={`${classes.actionIcon} ${classes.primaryAction}`}
        />
    </IconTooltip>
  );
};

const DeleteIcon = ({ onClick }: any) => {
  const classes = useStyles();
  return (
    <IconTooltip title="Delete">
        <BsFillTrashFill
          className={`${classes.actionIcon} ${classes.dangerAction}`}
          onClick={onClick}
        />
    </IconTooltip>
  );
};

const MoveIcon = ({ onClick }: any) => {
  const classes = useStyles();
  return (
    <IconTooltip title="Move Trucks">
        <BiMoveHorizontal
          className={`${classes.actionIcon} ${classes.neutralAction}`}
          onClick={onClick}
        />
    </IconTooltip>
  );
};

const PrintIcon = ({ onClick }: any) => {
  const classes = useStyles();
  return (
    <IconTooltip title="Print Record">
        <BsFillPrinterFill
          className={`${classes.actionIcon} ${classes.neutralAction}`}
          onClick={onClick}
        />
    </IconTooltip>
  );
};

const DigitalTicketIcon = ({ onClick }: any) => {
  const classes = useStyles();
  return (
    <IconTooltip title="View Digital Ticket">
        <BsFillStickyFill
          className={`${classes.actionIcon} ${classes.warningAction}`}
          onClick={onClick}
        />
    </IconTooltip>
  );
};

const ReceiveIcon = ({ onClick, isMobile }: any) => {
  const classes = useStyles();

  return (
    <Tooltip title="Receive Ticket" placement="top" arrow enterDelay={150}>
      <Button
        type="submit"
        variant="contained"
        color="secondary"
        onClick={onClick}
        className={isMobile ? classes.receiveIconCompact : classes.receiveIcon}
      >
        {isMobile ? <MdCallReceived size={18} /> : "Receive"}
      </Button>
    </Tooltip>
  );
};

const CheckIcon = ({ onClick, isMobile }: any) => {
  const classes = useStyles();
  return (
    <Tooltip title="Enter Headspace" placement="top" arrow enterDelay={150}>
      <Button
        type="button"
        variant="contained"
        color="default"
        onClick={onClick}
        className={isMobile ? classes.receiveIconCompact : classes.receiveIcon}
      >
        {isMobile ? <IoMdCheckmark size={18} /> : "Check"}
      </Button>
    </Tooltip>
  );
};

const ClearCheckIcon = ({ onClick }: any) => {
  const classes = useStyles();
  return (
    <IconTooltip title="Clear Headspace">
        <MdOutlinePlaylistRemove
          className={`${classes.actionIcon} ${classes.dangerAction}`}
          onClick={onClick}
        />
    </IconTooltip>
  );
};

const TicketLogsIcon = ({ onClick }: any) => {
  const classes = useStyles();

  return (
    <IconTooltip title="View Ticket Logs">
        <BsListOl
          className={`${classes.actionIcon} ${classes.primaryAction}`}
          onClick={onClick}
        />
    </IconTooltip>
  );
};

const ProjectLogsIcon = ({ onClick }: any) => {
  const classes = useStyles();

  return (
    <IconTooltip title="View Project Logs">
        <BsListOl
          className={`${classes.actionIcon} ${classes.primaryAction}`}
          onClick={onClick}
        />
    </IconTooltip>
  );
};

const ViewHaulageRecordIcon = ({ onClick }: any) => {
  const classes = useStyles();
  return (
    <IconTooltip title="View Haulage Record">
        <FaRegNewspaper
          className={`${classes.actionIcon} ${classes.neutralAction}`}
          onClick={onClick}
        />
    </IconTooltip>
  );
};

const PersonCheckFillIcon = () => {
  const classes = useStyles();
  return (
    <BsFillPersonCheckFill
      className={`${classes.statusIcon} ${classes.positiveStatus}`}
    />
  );
};

const PersonDashFillIcon = () => {
  const classes = useStyles();
  return (
    <BsFillPersonDashFill
      className={`${classes.statusIcon} ${classes.neutralStatus}`}
    />
  );
};

const SaltImpactedIcon = () => {
  const classes = useStyles();
  return (
    <IconTooltip title="Salt Impacted">
        <TbSalt className={`${classes.statusIcon} ${classes.positiveStatus}`} />
    </IconTooltip>
  );
};

const HaulageRecordReportIcon = ({ onClick }: any) => {
  const classes = useStyles();
  return (
    <IconTooltip title="Haulage Record Report">
        <BiSolidReport
          className={`${classes.actionIcon} ${classes.primaryAction}`}
          onClick={onClick}
        />
    </IconTooltip>
  );
};

const ArchiveIcon = () => {
  const classes = useStyles();
  return (
    <IconTooltip title="Archived">
        <FaArchive className={classes.archiveIcon} />
    </IconTooltip>
  );
};

// Soft, subtle pills with the full workstream name (matches the storage-area
// status pill look — pastel background + darker text, not a bold solid badge).
const streamPillStyle = {
  fontWeight: 500,
  marginRight: 4,
  marginBottom: 4,
} as const;

const MainPill = () => (
  <Chip label="Main Ticket" size="small" style={{ ...streamPillStyle, backgroundColor: "#e7edf5", color: "#032e5b" }} />
);

const SmallQuantityPill = () => (
  <Chip label="Small Quantity" size="small" style={{ ...streamPillStyle, backgroundColor: "#e3f2fd", color: "#0d47a1" }} />
);

const HydrovacPill = () => (
  <Chip label="Hydrovac" size="small" style={{ ...streamPillStyle, backgroundColor: "#eceff1", color: "#424242" }} />
);

const RubblePill = () => (
  <Chip label="Rubble" size="small" style={{ ...streamPillStyle, backgroundColor: "#fff4e5", color: "#e65100" }} />
);

const SpinningRefreshIcon = ({
  onClick,
  refreshing,
}: {
  onClick: () => void;
  refreshing: boolean;
}) => {
  useStyles();
  return (
    <RefreshIconMui
      onClick={onClick}
      style={{
        cursor: "pointer",
        animation: refreshing ? "spin 1s linear infinite" : "none",
      }}
    />
  );
};

export {
  ActionIconGroup,
  StatusIconGroup,
  ReceivedIcon,
  NotCompleteIcon,
  PendingHeadspaceIcon,
  SoilFLOIcon,
  TruckDispatchIcon,
  TicketEditIcon,
  EditIcon,
  DeleteIcon,
  ReceiveIcon,
  DigitalTicketIcon,
  TicketLogsIcon,
  ProjectLogsIcon,
  ViewHaulageRecordIcon,
  MoveIcon,
  PrintIcon,
  PersonCheckFillIcon,
  PersonDashFillIcon,
  OverThresholdIcon,
  SaltImpactedIcon,
  HaulageRecordReportIcon,
  ArchiveIcon,
  SpinningRefreshIcon,
  CheckIcon,
  ClearCheckIcon,
  ScreeningFlagIcon,
  MainPill,
  SmallQuantityPill,
  HydrovacPill,
  RubblePill,
};
